import React from 'react'
import { ChevronDown } from 'lucide-react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import EpochMonthService from '../../../../services/EpochMonth.service'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import { cn } from '../../../../lib/utils'
import { ClientLevelItem } from '../../../../interfaces/dashboards/PerformanceDashboard.interface'

const PERIODS = ['3M', '6M', 'YTD', 'Last 12 M'] as const
const METRICS = ['Sales', 'Inventory', 'Cash margin', 'Inflow'] as const
export const RETAIL_PERFORMANCE_X_AXIS_INTERVAL = 0
export const RETAIL_PERFORMANCE_LEGEND = {
  lastYear: 'Last Year',
  actual: 'Actual',
  plan: 'Plan',
} as const

export function getRetailPerformanceTooltipLabel(dataKey: string | number | undefined) {
  if (dataKey === 'actual') return RETAIL_PERFORMANCE_LEGEND.actual
  if (dataKey === 'plan') return RETAIL_PERFORMANCE_LEGEND.plan
  return RETAIL_PERFORMANCE_LEGEND.lastYear
}

export type RetailPerformancePeriod = (typeof PERIODS)[number]
export type RetailPerformanceMetric = (typeof METRICS)[number]

export interface RetailPerformanceChartRow {
  epochMonthSpan: number;
  label: string;
  actual: number;
  plan: number;
  lastYear: number;
  hasData?: boolean;
}

type RetailPerformanceSourceRow = ClientLevelItem & {
  isMissingPeriodMonth?: boolean;
}

export interface RetailPerformanceSummaryCard {
  label: string;
  value: string;
  caption: string;
  valueClassName?: string;
  captionClassName?: string;
}

export function getRetailPerformanceRowsForPeriod(
  rows: ClientLevelItem[] = [],
  period: RetailPerformancePeriod = '6M',
): ClientLevelItem[] {
  const sorted = [...rows].sort((a, b) => (a.epochMonthSpan || 0) - (b.epochMonthSpan || 0))
  if (sorted.length === 0) return []

  const lastCompletedEpochMonth = Math.max(EpochMonthService.getTodayEpochMonth() - 1, 0)
  const completedRows = sorted.filter((row) => Number(row.epochMonthSpan) <= lastCompletedEpochMonth)
  const scopedRows = completedRows.length > 0 ? completedRows : sorted
  const anchorEpochMonth = Number(scopedRows[scopedRows.length - 1]?.epochMonthSpan) || 0

  if (period === '3M') return scopedRows.slice(-3)
  if (period === '6M') return scopedRows.slice(-6)
  if (period === 'Last 12 M') {
    const startEpochMonth = Math.max(lastCompletedEpochMonth - 11, 0)
    const rowsByEpochMonth = new Map(
      scopedRows.map((row) => [Number(row.epochMonthSpan), row]),
    )

    return Array.from({ length: lastCompletedEpochMonth - startEpochMonth + 1 }, (_, index) => {
      const epochMonthSpan = startEpochMonth + index
      const existingRow = rowsByEpochMonth.get(epochMonthSpan)

      if (existingRow) return existingRow

      return {
        epochMonthSpan,
        isMissingPeriodMonth: true,
      } as RetailPerformanceSourceRow
    })
  }

  const anchorDate = EpochMonthService.epochMonthToDate(anchorEpochMonth)
  const startOfYearEpochMonth = anchorEpochMonth - anchorDate.getMonth()
  return scopedRows.filter((row) => Number(row.epochMonthSpan) >= startOfYearEpochMonth)
}

export function buildRetailPerformanceChartRows(
  rows: ClientLevelItem[] = [],
  metric: RetailPerformanceMetric = 'Inventory',
): RetailPerformanceChartRow[] {
  const selectors: Record<RetailPerformanceMetric, {
    actual: (row: ClientLevelItem) => number;
    plan: (row: ClientLevelItem) => number;
    lastYear: (row: ClientLevelItem) => number;
  }> = {
    Sales: {
      actual: (row) => Number(row.salesRm) || 0,
      plan: (row) => Number(row.salesPlan) || 0,
      lastYear: (row) => Number(row.salesLy) || 0,
    },
    Inventory: {
      actual: (row) => Number(row.inventoryBOMRM) || 0,
      plan: (row) => Number(row.inventoryBOMPlan) || 0,
      lastYear: (row) => Number(row.inventoryBomLy) || 0,
    },
    'Cash margin': {
      actual: (row) => Number(row.cashMargin) || (Number(row.salesRm) - Number(row.inflowRetailRm)) || 0,
      plan: (row) => Number(row.cashMarginPlan) || (Number(row.salesPlan) - Number(row.inflowPlanRetailRm)) || 0,
      lastYear: (row) => Number(row.cashMarginLy) || (Number(row.salesLy) - Number(row.inflowRetailLy)) || 0,
    },
    Inflow: {
      actual: (row) => Number(row.inflowRetailRm) || 0,
      plan: (row) => Number(row.inflowPlanRetailRm) || 0,
      lastYear: (row) => Number(row.inflowRetailLy) || 0,
    },
  }

  const selector = selectors[metric]

  return rows.map((row) => {
    const isMissingPeriodMonth = Boolean((row as RetailPerformanceSourceRow).isMissingPeriodMonth)

    return {
      epochMonthSpan: Number(row.epochMonthSpan) || 0,
      label: Number.isFinite(Number(row.epochMonthSpan)) ? EpochMonthService.epochMonthToMMM(row.epochMonthSpan) : '',
      actual: isMissingPeriodMonth ? Number.NaN : selector.actual(row),
      plan: isMissingPeriodMonth ? Number.NaN : selector.plan(row),
      lastYear: isMissingPeriodMonth ? Number.NaN : selector.lastYear(row),
      hasData: !isMissingPeriodMonth,
    }
  })
}

export function buildRetailPerformanceSummaryCards(
  chartRows: RetailPerformanceChartRow[] = [],
  metric: RetailPerformanceMetric = 'Inventory',
  period: RetailPerformancePeriod = '6M',
): RetailPerformanceSummaryCard[] {
  const metricLabel = metric === 'Cash margin' ? 'Cash Margin' : metric
  const availableChartRows = chartRows.filter((row) => row.hasData !== false)

  if (availableChartRows.length === 0) {
    return [
      { label: `${metricLabel} Plan`, value: '—', caption: 'No data loaded' },
      { label: 'Trailing Actual', value: '—', caption: 'No data loaded' },
      { label: 'Variance to Plan', value: '—', caption: 'vs plan' },
      { label: 'Last Year', value: '—', caption: 'No data loaded' },
      { label: 'Variance to LY', value: '—', caption: 'vs LY' },
      { label: 'Period', value: period, caption: 'No data loaded' },
    ]
  }

  const totals = availableChartRows.reduce((acc, row) => ({
    actual: acc.actual + (Number(row.actual) || 0),
    plan: acc.plan + (Number(row.plan) || 0),
    lastYear: acc.lastYear + (Number(row.lastYear) || 0),
  }), { actual: 0, plan: 0, lastYear: 0 })

  const divisor = metric === 'Inventory' ? availableChartRows.length || 1 : 1
  const actualValue = totals.actual / divisor
  const planValue = totals.plan / divisor
  const lastYearValue = totals.lastYear / divisor

  const varianceToPlan = actualValue - planValue
  const varianceToLy = actualValue - lastYearValue
  const periodCaption = formatRetailPerformancePeriodLabel(chartRows)
  const periodValue = period === 'YTD'
    ? 'YTD'
    : period === 'Last 12 M'
      ? '12 mo'
      : period.toLowerCase()

  return [
    {
      label: `${metricLabel} Plan`,
      value: PerformanceDashboardService.moneyFormat(planValue, true),
      caption: periodCaption,
    },
    {
      label: 'Trailing Actual',
      value: PerformanceDashboardService.moneyFormat(actualValue, true),
      caption: periodCaption,
    },
    {
      label: 'Variance to Plan',
      value: formatSignedCurrency(varianceToPlan),
      valueClassName: 'text-[#344054]',
      caption: 'vs plan',
      captionClassName: varianceToPlan >= 0 ? 'text-[#079455]' : 'text-[#F04438]',
    },
    {
      label: 'Last Year',
      value: PerformanceDashboardService.moneyFormat(lastYearValue, true),
      valueClassName: 'text-[18px] leading-[100%] tracking-[-0.45px] text-[#344054]',
      caption: formatSignedCurrency(varianceToLy) + ' vs LY',
      captionClassName: varianceToLy >= 0 ? 'text-[#079455]' : 'text-[#F04438]',
    },
    {
      label: 'Variance to LY',
      value: formatSignedCurrency(varianceToLy),
      valueClassName: 'text-[18px] leading-[100%] tracking-[-0.45px] text-[#344054]',
      caption: formatSignedCurrency(varianceToPlan) + " vs period's plan",
      captionClassName: varianceToPlan >= 0 ? 'text-[#079455]' : 'text-[#F04438]',
    },
    {
      label: 'Period',
      value: periodValue,
      valueClassName: 'text-[#344054]',
      caption: periodCaption,
      captionClassName: 'tracking-[0.12px]',
    },
  ]
}

export function formatRetailPerformancePeriodLabel(rows: RetailPerformanceChartRow[] = []) {
  if (rows.length === 0) return 'No period selected'
  const first = rows[0]
  const last = rows[rows.length - 1]
  const firstDate = EpochMonthService.epochMonthToDate(first.epochMonthSpan)
  const lastDate = EpochMonthService.epochMonthToDate(last.epochMonthSpan)

  if (first.epochMonthSpan === last.epochMonthSpan) {
    return `${EpochMonthService.epochMonthToMMM(first.epochMonthSpan)} ${firstDate.getFullYear()}`
  }

  if (firstDate.getFullYear() === lastDate.getFullYear()) {
    return `${EpochMonthService.epochMonthToMMM(first.epochMonthSpan)}–${EpochMonthService.epochMonthToMMM(last.epochMonthSpan)} ${lastDate.getFullYear()}`
  }

  return `${EpochMonthService.epochMonthToMMM(first.epochMonthSpan)} ${firstDate.getFullYear()}–${EpochMonthService.epochMonthToMMM(last.epochMonthSpan)} ${lastDate.getFullYear()}`
}

function formatSignedCurrency(value: number) {
  const prefix = value >= 0 ? '+' : '-'
  return `${prefix}${PerformanceDashboardService.moneyFormat(Math.abs(value), true).replace('$', '$')}`
}

function StatCard({
  label,
  value,
  valueClassName,
  caption,
  captionClassName,
}: RetailPerformanceSummaryCard) {
  return (
    <div className="flex min-w-0 flex-col items-start gap-1.5 rounded-[7.25px] bg-[#F9FAFB] px-2.5 pb-3 pt-2.5">
      <div className="text-[10px] font-medium uppercase leading-[15px] tracking-[0.4px] text-[#667085]">
        {label}
      </div>
      <div className={cn('text-[15px] font-bold leading-[22.5px] text-[#333]', valueClassName)}>
        {value}
      </div>
      <div className={cn('text-xs font-medium leading-4 text-[#667085]', captionClassName)}>
        {caption}
      </div>
    </div>
  )
}

function Pill({
  options,
  active,
  onSelect,
}: {
  options: readonly string[];
  active: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex items-start gap-1 rounded-[14px] border border-[#EAECF0] bg-[#F2F4F7] p-1">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onSelect(option)}
          className={cn(
            'flex items-center justify-center whitespace-nowrap rounded-[10px] px-3.5 py-1.5 text-[13px] font-medium leading-[18.75px] tracking-[-0.037px] transition-colors',
            active === option
              ? 'bg-white text-[#7F56D9] shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]'
              : 'text-[#667085] hover:text-[#344054]',
          )}
        >
          {option}
        </button>
      ))}
    </div>
  )
}

function RetailPerformanceChart({ rows, metric }: { rows: RetailPerformanceChartRow[]; metric: RetailPerformanceMetric }) {
  if (rows.length === 0 || rows.every((row) => row.hasData === false)) {
    return <div className="flex h-[260px] items-center justify-center rounded-md border border-dashed border-gray-200 text-sm text-gray-500">No monthly retail performance data is available.</div>
  }

  return (
    <div className="h-[260px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows}>
          <defs>
            <linearGradient id="retailPerformanceActual" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366F1" stopOpacity={0.32} />
              <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="retailPerformanceLastYear" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F79009" stopOpacity={0.18} />
              <stop offset="95%" stopColor="#F79009" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="0" stroke="#EAECF0" vertical={false} />
          <XAxis dataKey="label" interval={RETAIL_PERFORMANCE_X_AXIS_INTERVAL} stroke="#667085" tick={{ fill: '#667085', fontSize: 12 }} tickLine={false} axisLine={false} />
          <YAxis hide />
          <Tooltip
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
            formatter={(value: number, _name: string, entry: { dataKey?: string | number }) => [
              PerformanceDashboardService.moneyFormat(Number(value) || 0, true),
              getRetailPerformanceTooltipLabel(entry?.dataKey),
            ]}
            cursor={false}
          />
          <Area type="monotone" dataKey="lastYear" stroke="#F79009" fill="url(#retailPerformanceLastYear)" strokeWidth={2} name={RETAIL_PERFORMANCE_LEGEND.lastYear} />
          <Area type="monotone" dataKey="actual" stroke="#6366F1" fill="url(#retailPerformanceActual)" strokeWidth={2} name={RETAIL_PERFORMANCE_LEGEND.actual} />
          <Area type="monotone" dataKey="plan" stroke="#7F56D9" fillOpacity={0} strokeWidth={2} name={RETAIL_PERFORMANCE_LEGEND.plan} />
          <Legend wrapperStyle={{ paddingTop: '16px' }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function RetailPerformanceSection({
  monthlyRows,
  loading,
  locationLabel = 'All locations',
  onLocationFilterClick,
}: {
  monthlyRows: ClientLevelItem[];
  loading: boolean;
  locationLabel?: string;
  onLocationFilterClick?: () => void;
}) {
  const [period, setPeriod] = React.useState<RetailPerformancePeriod>('6M')
  const [metric, setMetric] = React.useState<RetailPerformanceMetric>('Inventory')

  const filteredRows = React.useMemo(() => getRetailPerformanceRowsForPeriod(monthlyRows, period), [monthlyRows, period])
  const chartRows = React.useMemo(() => buildRetailPerformanceChartRows(filteredRows, metric), [filteredRows, metric])
  const summaryCards = React.useMemo(() => buildRetailPerformanceSummaryCards(chartRows, metric, period), [chartRows, metric, period])

  return (
    <section className="flex w-full flex-col items-start rounded-[20px] border border-[#EAECF0] bg-white p-5 shadow-[0_1px_2px_0_rgba(16,24,40,0.06),0_1px_3px_0_rgba(16,24,40,0.04)] sm:p-6 lg:p-8">
      <div className="flex w-full flex-col items-start justify-between gap-4 lg:flex-row lg:items-start">
        <div className="flex flex-col items-start">
          <h2 className="text-xl font-semibold leading-[30px] tracking-[-0.649px] text-[#101828]">Retail Performance</h2>
          <p className="pt-1 text-sm font-normal leading-[150%] tracking-[-0.112px] text-[#667085]">Your retail performance over time</p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {onLocationFilterClick ? (
            <button
              type="button"
              onClick={onLocationFilterClick}
              className="flex items-center gap-1.5 rounded-[10px] border border-[#D0D5DD] bg-white px-3 py-[7px] text-center text-[13px] font-medium leading-[18.75px] tracking-[-0.037px] text-[#344054]"
            >
              {locationLabel}
              <ChevronDown className="h-4 w-4 text-[#344054]" strokeWidth={1.33} />
            </button>
          ) : (
            <span className="inline-flex items-center rounded-[10px] border border-[#D0D5DD] bg-white px-3 py-[7px] text-[13px] font-medium leading-[18.75px] tracking-[-0.037px] text-[#344054]">
              {locationLabel}
            </span>
          )}
          <Pill options={PERIODS} active={period} onSelect={(value) => setPeriod(value as RetailPerformancePeriod)} />
        </div>
      </div>

      <div className="flex w-full justify-start pt-5">
        <Pill options={METRICS} active={metric} onSelect={(value) => setMetric(value as RetailPerformanceMetric)} />
      </div>

      <div className="grid w-full grid-cols-2 gap-[7px] pt-5 sm:grid-cols-3 lg:grid-cols-6">
        {summaryCards.map((card) => <StatCard key={card.label} {...card} />)}
      </div>

      <div className="w-full pt-6">
        {loading
          ? <div className="flex h-[260px] items-center justify-center rounded-md border border-gray-200 text-sm text-gray-500">Loading retail performance trend…</div>
          : <RetailPerformanceChart rows={chartRows} metric={metric} />}
      </div>
    </section>
  )
}



